import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { KamaGuide } from '../../types/types';
import CopticText from './CopticText';
import './KamaGuideCard.css';

interface KamaGuideCardProps {
  title: string;
  guide: KamaGuide;
  // Controlled by KamaLearnMode, whose Suivant/Précédent step through the
  // bubbles before turning the page. Without it (standalone use) the card
  // keeps its own state and shows a « Continuer » button instead.
  step?: number;
  onStepChange?: (step: number) => void;
}

// French typography: « » and the high punctuation (! ? : ;) take a space
// that must never break — otherwise the bubble wrapped « pour le dieu »
// with the closing » alone at the start of the next line.
const NBSP = ' ';
const keepFrenchSpacing = (text: string) =>
  text
    .replace(/« /g, `«${NBSP}`)
    .replace(/ »/g, `${NBSP}»`)
    .replace(/ ([!?:;])/g, `${NBSP}$1`);

// A rule taught by the app's mascot (the ibis) instead of a paragraph to
// read: the example sentence is laid out word by word (glyphs,
// transliteration, gloss) and each speech bubble lights up the words it
// talks about. In a lesson the book's own Suivant/Précédent step through
// the bubbles (see KamaLearnMode's goNext) — a second « Continuer » button
// beside Suivant read as two competing ways forward. Rendered by
// HieroglyphCard for any entry carrying `guide` (see phrase.ts).
//
// The root keeps the `.hgw` class so HieroglyphCard.css's
// `.hgw button * { pointer-events: none }` applies: react-pageflip only
// spares a click from turning the page when its target is the <button>
// itself, not a span inside it.
const KamaGuideCard: React.FC<KamaGuideCardProps> = ({ title, guide, step: controlledStep, onStepChange }) => {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language.startsWith('en');
  const [ownStep, setOwnStep] = useState(0);
  const isControlled = controlledStep !== undefined;
  const stepIndex = Math.min(isControlled ? controlledStep : ownStep, guide.steps.length - 1);
  const setStepIndex = (next: number) => (isControlled ? onStepChange?.(next) : setOwnStep(next));

  const step = guide.steps[stepIndex];
  // A step without `sentence` keeps showing the previous step's sentence.
  const sentenceIndex = guide.steps
    .slice(0, stepIndex + 1)
    .reduce((current, s) => s.sentence ?? current, 0);
  const sentence = guide.sentences[sentenceIndex];
  const highlight = new Set(step.highlight ?? []);
  const isLast = stepIndex === guide.steps.length - 1;

  return (
    <div className="hgw hgw--guide">
      <p className="kg__title">{title}</p>

      {sentence && (
        // Keyed by sentence so switching to the second example replays the
        // entrance animation instead of swapping words in place.
        <div className="kg__sentence" key={sentenceIndex}>
          <div className="kg__words">
            {sentence.words.map((word, i) => (
              <div
                key={i}
                className={[
                  'kg__word',
                  highlight.has(i) ? 'is-lit' : '',
                  highlight.size > 0 && !highlight.has(i) ? 'is-dim' : '',
                ].filter(Boolean).join(' ')}
              >
                <span className="kg__glyphs">{word.glyphs}</span>
                <span className="kg__translit">{word.translit}</span>
                <span className="kg__gloss"><CopticText text={isEn ? word.glossEn : word.gloss} /></span>
              </div>
            ))}
          </div>
          <p className="kg__translation">{keepFrenchSpacing(`« ${isEn ? sentence.translationEn : sentence.translation} »`)}</p>
        </div>
      )}

      <div className="kg__mascot-row">
        <img className="kg__mascot" src="/Mascotte/mascotte5.png" alt="" aria-hidden="true" />
        <div className="kg__bubble" aria-live="polite">
          {/* Keyed by step so each new line pops in. */}
          <p className="kg__bubble-text" key={stepIndex}>
            <CopticText text={isEn ? step.textEn : keepFrenchSpacing(step.text)} />
          </p>
          <div className="kg__bubble-footer">
            <span className="kg__dots" aria-hidden="true">
              {guide.steps.map((_, i) => (
                <span key={i} className={`kg__dot ${i <= stepIndex ? 'is-on' : ''}`} />
              ))}
            </span>
            {isLast ? (
              guide.steps.length > 1 && (
                <button type="button" className="kg__btn kg__btn--ghost" onClick={() => setStepIndex(0)}>
                  {t('kamaGuide.replay')}
                </button>
              )
            ) : (
              !isControlled && (
                <button type="button" className="kg__btn" onClick={() => setStepIndex(stepIndex + 1)}>
                  {t('kamaGuide.continue')}
                </button>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// The ibis cheering a correct quiz answer (HieroglyphCard's quiz cards):
// same sprite and bubble as the guided lessons, plus a hop. The cheer is
// picked from the entry key, not at random, so it doesn't change on every
// re-render.
export const IbisCheer: React.FC<{ seed: string; children: React.ReactNode }> = ({ seed, children }) => {
  const { t } = useTranslation();
  const cheers = t('kamaGuide.cheers', { returnObjects: true });
  const list = Array.isArray(cheers) && cheers.length > 0 ? (cheers as string[]) : [t('kamaWordTest.correct')];
  const hash = Array.from(seed).reduce((h, ch) => (h * 31 + ch.charCodeAt(0)) >>> 0, 0);

  // Appears below the four answers, often under the lesson's sticky
  // Suivant/Terminer bar: scroll the lesson overlay just enough to show it
  // above that bar. The overlay is scrolled directly — scrollIntoView would
  // also scroll the book's overflow-hidden clip (KamaLearnMode) and shift
  // the page out of place. Waits for the clip's 0.25s height transition.
  const rowRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const row = rowRef.current;
      const overlay = row?.closest('.kama-category-preview-overlay');
      if (!row || !overlay) return;
      const navHeight = overlay.querySelector('.kama-lesson__nav--sticky')?.getBoundingClientRect().height ?? 0;
      const overflow = row.getBoundingClientRect().bottom - (overlay.getBoundingClientRect().bottom - navHeight - 12);
      if (overflow > 0) overlay.scrollBy({ top: overflow, behavior: 'smooth' });
    }, 300);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <div className="kg__mascot-row kg__mascot-row--cheer" ref={rowRef}>
      <img className="kg__mascot kg__mascot--cheer" src="/Mascotte/mascotte5.png" alt="" aria-hidden="true" />
      <div className="kg__bubble kg__bubble--cheer" role="status">
        <p className="kg__cheer">{keepFrenchSpacing(list[hash % list.length])}</p>
        <div className="kg__bubble-text">{children}</div>
      </div>
    </div>
  );
};

export default KamaGuideCard;
